/**
 * Same-origin API rewriter for production deployment on cricketdrive.org.
 *
 * Why this exists
 * ---------------
 * Our PROD frontend bundle on https://cricketdrive.org was built with
 * REACT_APP_BACKEND_URL pointing to https://neo-coaching-hub.emergent.host
 * (Emergent's auto-assigned URL). That backend's Kubernetes ingress returns:
 *
 *   Access-Control-Allow-Origin: *
 *   Access-Control-Allow-Credentials: true
 *
 * which is INVALID per CORS spec when cookies are involved. The browser
 * silently blocks the response and all auth flows fail with the generic
 * "An error occurred" toast.
 *
 * The cleanest fix is same-origin API calls. cricketdrive.org's ingress
 * routes /api/* to the same backend pod that neo-coaching-hub does, but
 * because it's same-origin the browser doesn't run a CORS check at all.
 *
 * This module intercepts the global `fetch` and `XMLHttpRequest.open` at
 * boot time and rewrites any URL that starts with the baked-in
 * REACT_APP_BACKEND_URL to use the current `window.location.origin`
 * instead — but ONLY when the page is being served from cricketdrive.org.
 * Preview environments remain untouched so QA isn't affected.
 *
 * Import this file ONCE at the very top of index.js (before App.js).
 */

const BACKEND_FROM_ENV = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const shouldRewrite = (() => {
  if (typeof window === 'undefined') return false;
  if (!BACKEND_FROM_ENV) return false;
  const host = window.location.hostname.toLowerCase();
  // Only rewrite on the production custom domain; preview keeps explicit URL
  if (host !== 'cricketdrive.org' && host !== 'www.cricketdrive.org') return false;
  // If env already matches current origin, nothing to do
  if (BACKEND_FROM_ENV === window.location.origin) return false;
  return true;
})();

if (shouldRewrite) {
  const origin = window.location.origin;

  const rewriteUrl = (url) => {
    if (typeof url !== 'string') return url;
    // Absolute URL pointing to the wrong host → swap to same-origin
    if (url.startsWith(BACKEND_FROM_ENV)) {
      return origin + url.slice(BACKEND_FROM_ENV.length);
    }
    return url;
  };

  // Patch global fetch (covers all fetch() calls everywhere)
  const originalFetch = window.fetch.bind(window);
  window.fetch = function patchedFetch(input, init) {
    try {
      if (typeof input === 'string') {
        return originalFetch(rewriteUrl(input), init);
      }
      if (typeof Request !== 'undefined' && input instanceof Request) {
        if (input.url.startsWith(BACKEND_FROM_ENV)) {
          return originalFetch(new Request(rewriteUrl(input.url), input), init);
        }
      }
    } catch (_e) {
      // Fall through to original on any unexpected input
    }
    return originalFetch(input, init);
  };

  // Patch XMLHttpRequest.open (covers axios + any legacy XHR-based libs)
  const OriginalOpen = window.XMLHttpRequest.prototype.open;
  window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
    return OriginalOpen.call(this, method, rewriteUrl(url), ...rest);
  };

  // Surface a one-time console hint for debugging
  // eslint-disable-next-line no-console
  console.info(
    `[apiOriginFix] Rewriting API calls from ${BACKEND_FROM_ENV} → ${origin} (same-origin).`
  );
}

export const isApiOriginRewritten = shouldRewrite;
